import {
    EAppointmentSlot,
    EAppointmentStatus,
    EInstallationType,
    IAppointment,
} from "@modules/eirCustomersFacade/interfaces/models/IAppointment";
import { TFunction } from "react-i18next";
import {
    ETimeSlot,
    IAvailableAppointmentDate,
} from "@modules/eirCustomersFacade/interfaces/models/IAvailableAppointmentDate";

export const mapBannerTitle = (appointment: IAppointment, t: TFunction<"appointment">): string => {
    switch (appointment.status) {
        case EAppointmentStatus.VALIDATED: {
            if (appointment.installationType === EInstallationType.SELF) {
                return t("validatedSelfInstallTitle");
            }

            return t("validatedTechInstallTitle");
        }
        case EAppointmentStatus.AWAITING_RESCHEDULE:
            return t("awaitingRescheduleTitle");
        case EAppointmentStatus.LOCKED:
            return t("lockedTitle");
        default:
            return "";
    }
};

export const mapBannerSubtitle = (
    appointmentStatus: EAppointmentStatus,
    t: TFunction<"appointment">
): string | undefined => {
    switch (appointmentStatus) {
        case EAppointmentStatus.AWAITING_RESCHEDULE:
            return t("awaitingRescheduleSubtitle");
        case EAppointmentStatus.LOCKED:
            return t("lockedSubtitle");
        default:
            return undefined;
    }
};

export const mapModalTitle = (installationType: EInstallationType, t: TFunction<"appointment">): string => {
    return installationType === EInstallationType.SELF
        ? t("rescheduleSelfInstallModalTitle")
        : t("rescheduleTechInstallModalTitle");
};

export const mapTimeSlotLabel = (timeSlot: EAppointmentSlot): string => {
    switch (timeSlot) {
        case EAppointmentSlot.AM:
            return "8:30 - 13:30";
        case EAppointmentSlot.PM:
            return "12:30 - 17:00";
        case EAppointmentSlot.AD:
            return "8:30 - 17:30";
        default:
            return "";
    }
};

export const mapButtonLabel = (appointmentStatus: EAppointmentStatus, t: TFunction<"appointment">): string => {
    switch (appointmentStatus) {
        case EAppointmentStatus.AWAITING_RESCHEDULE:
            return t("buttonReschedule");
        default:
            return t("buttonChange");
    }
};

export const buildMap = (appointments: IAvailableAppointmentDate[]): Map<string, ETimeSlot[]> => {
    const result = new Map<string, ETimeSlot[]>();
    for (const appointment of appointments || []) {
        result.set(appointment.scheduledDate, appointment.timeSlotList || []);
    }

    return result;
};

export const getFirstAvailableDateInList = (availableAppointmentDateList: IAvailableAppointmentDate[]): string => {
    if (availableAppointmentDateList.length === 0) {
        return "";
    }

    return availableAppointmentDateList
        .map((availableAppointment) => availableAppointment.scheduledDate)
        .reduce((smallestAvailableDate, currentAvailableDate) =>
            currentAvailableDate < smallestAvailableDate ? currentAvailableDate : smallestAvailableDate
        );
};

const enumOrder: readonly ETimeSlot[] = [ETimeSlot.AM, ETimeSlot.PM, ETimeSlot.AD];

export const sortTimeSlots = (timeSlotList: ETimeSlot[]): ETimeSlot[] =>
    [...timeSlotList].sort((a, b) => enumOrder.indexOf(a) - enumOrder.indexOf(b));

export const getFirstAvailableTimeSlot = (timeSlotList: ETimeSlot[]): ETimeSlot => {
    const sortedTimeSlots = sortTimeSlots(timeSlotList);

    return sortedTimeSlots[0];
};

export const getValueOfMap = (map: Map<string, ETimeSlot[]>, date: string): ETimeSlot[] => map.get(date) ?? [];
